import React, { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS, TOKEN_KEY } from '@/config/api'
import type { User, LoginRequest, LoginResponse } from '@/types'
import { SimulazioneContext } from '@/contexts/SimulazioneContext'
import { azzeraStatoSimulazione } from '@/lib/statoSimulazione'

interface AuthContextType {
  user: User | null
  /** Permessi in vigore: quelli del ruolo simulato se la simulazione e' attiva, altrimenti i propri */
  permissions: string[]
  /** Permessi veri dell'utente, mai sostituiti dalla simulazione */
  permissionsReali: string[]
  loading: boolean
  login: (credentials: LoginRequest) => Promise<void>
  loginWithExternalToken: (token: string) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [permissions, setPermissions] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  // SimulazioneProvider sta piu' in alto in App.tsx. Quando la simulazione e'
  // attiva espone i permessi del ruolo scelto: da qui in giu' tutta la UI li
  // legge al posto dei propri. L'utente resta se stesso: `user` non cambia,
  // e i permessi veri restano disponibili in `permissionsReali`.
  const simulazione = useContext(SimulazioneContext)
  const permessiInVigore = simulazione?.permessiSimulati ?? permissions

  // Uscendo dall'account la simulazione va spenta: e' uno stato della sessione
  // di chi stava simulando, non del browser. Senza questo, chi usciva con la
  // simulazione accesa si ritrovava il login rifiutato dall'interceptor —
  // il logout naviga senza ricaricare la pagina, quindi ne' lo stato React
  // ne' quello di modulo sparivano da soli.
  //
  // Si chiamano tutt'e due: `esci()` riporta l'interfaccia ai permessi veri,
  // `azzeraStatoSimulazione()` spegne subito il blocco delle scritture senza
  // aspettare il useEffect del contesto, che gira solo dopo il render.
  const spegniSimulazione = () => {
    simulazione?.esci()
    azzeraStatoSimulazione()
  }

  // Carica utente al mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (token) {
      loadUser()
    } else {
      setLoading(false)
    }
  }, [])

  // isRefresh distingue il primo caricamento dal ricaricamento a sessione
  // gia' avviata. PermissionRoute legge i permessi da qui: azzerarli con
  // loading a false manderebbe tutte le rotte protette su ForbiddenPage,
  // senza che l'utente capisca perche'. Al primo caricamento l'array vuoto
  // e' il valore corretto; a un refresh fallito si conserva quello che c'e',
  // perche' l'autorita' resta il backend, che rifiuta comunque le chiamate
  // non autorizzate.
  const loadUser = async (isRefresh = false) => {
    try {
      const response = await axiosInstance.get<User>(API_ENDPOINTS.ME)
      setUser(response.data)
      
      // Carica permissions
      try {
        const permResponse = await axiosInstance.get<any>('/permissions/me')
        setPermissions(permResponse.data.permissions?.map((p: any) => p.codice) || [])
      } catch (permError) {
        if (isRefresh) {
          console.warn(
            "Ricaricamento permessi fallito: mantengo quelli gia' in uso.",
            permError,
          )
        } else {
          setPermissions([])
        }
      }
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY)
      spegniSimulazione()
      setUser(null)
      setPermissions([])
    } finally {
      setLoading(false)
    }
  }

  const login = async (credentials: LoginRequest) => {
    try {
      setLoading(true)
      
      // Invia JWT JSON body
      const response = await axiosInstance.post<LoginResponse>(
        API_ENDPOINTS.LOGIN,
        {
          username: credentials.username,
          password: credentials.password,
        }
      )

      const { access_token, user: userData } = response.data

      // Salva token
      localStorage.setItem(TOKEN_KEY, access_token)
      setUser(userData)

      message.success(`Benvenuto, ${userData.nome}!`)
      
      // Carica permissions
      try {
        const permResponse = await axiosInstance.get<any>('/permissions/me')
        setPermissions(permResponse.data.permissions?.map((p: any) => p.codice) || [])
      } catch (permError) {
        setPermissions([])
      }
      
      const returnTo = new URLSearchParams(window.location.search).get('returnTo')
      navigate(returnTo && returnTo.startsWith('/') ? returnTo : '/')
    } catch (error: any) {
      if (error.response?.status === 401) {
        message.error({ content: 'Email o password non corretti', duration: 20 })
      } else if (error.response?.status !== 429) {
        message.error({
          content: `Errore durante il login: ${error.response?.data?.detail || error.message}`,
          duration: 20,
        })
      }
      // 429: LandingPage gestisce con Alert dedicato — nessun toast qui
      throw error
    } finally {
      setLoading(false)
    }
  }

  const loginWithExternalToken = async (token: string) => {
    localStorage.setItem(TOKEN_KEY, token)
    await loadUser()
    navigate('/')
  }

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY)
    spegniSimulazione()
    setUser(null)
    setPermissions([])
    message.info('Logout effettuato con successo')
    navigate('/login')
  }

  const refreshUser = async () => {
    await loadUser(true)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        permissions: permessiInVigore,
        permissionsReali: permissions,
        loading,
        login,
        loginWithExternalToken,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
